import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, X } from 'lucide-react';
import { useDemoModal } from './demo-modal-context';
import { cn } from '../lib/cn';

const PMS_OPTIONS = [
  'PioneerRx',
  'QS/1',
  'Liberty',
  'Computer-Rx',
  'BestRx',
  'Epic Willow',
  'Other',
] as const;

const VOLUME_OPTIONS = [
  'Under 100',
  '100–300',
  '300–700',
  '700–1,500',
  '1,500+',
] as const;

const demoSchema = z.object({
  fullName: z.string().min(2, 'Required'),
  pharmacyName: z.string().min(2, 'Required'),
  workEmail: z.string().email('Enter a valid email'),
  phone: z.string().optional(),
  pms: z.string().optional(),
  volume: z.string().optional(),
  notes: z.string().max(1000).optional(),
});

type DemoFormValues = z.infer<typeof demoSchema>;

export function DemoModal() {
  const { isOpen, close } = useDemoModal();
  const [submitted, setSubmitted] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<DemoFormValues>({
    resolver: zodResolver(demoSchema),
  });

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, close]);

  useEffect(() => {
    if (!isOpen) {
      setSubmitted(false);
      reset();
    }
  }, [isOpen, reset]);

  if (!isOpen) return null;

  const onSubmit = async (_values: DemoFormValues) => {
    await new Promise((r) => setTimeout(r, 400));
    setSubmitted(true);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-modal-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/60 p-0 backdrop-blur-sm md:items-center md:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-lg overflow-hidden rounded-t-2xl bg-white shadow-2xl ring-1 ring-border md:rounded-2xl"
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-md p-1.5 text-ink-2 transition-colors hover:bg-bg-muted hover:text-ink"
        >
          <X className="h-4 w-4" />
        </button>

        {submitted ? (
          <div className="px-8 py-12 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-primary" strokeWidth={1.5} />
            <h2 id="demo-modal-title" className="mt-4 text-xl font-semibold text-ink">
              Got it. We'll be in touch within one business day.
            </h2>
            <p className="mt-2 text-sm text-ink-2">
              In the meantime, feel free to email{' '}
              <a className="text-primary underline" href="mailto:hello@lynrx.co">
                hello@lynrx.co
              </a>{' '}
              with anything specific.
            </p>
            <button
              type="button"
              onClick={close}
              className="mt-6 rounded-lg bg-ink px-4 py-2 text-sm font-medium text-white hover:bg-ink/90"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="px-6 py-6 md:px-8 md:py-8">
            <h2 id="demo-modal-title" className="text-lg font-semibold text-ink">
              Request a demo
            </h2>
            <p className="mt-1 text-sm text-ink-2">
              30 minutes. We walk through your pharmacy operation and show how LYN Rx fits.
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
              <Field label="Full name" error={errors.fullName?.message}>
                <input
                  {...register('fullName')}
                  type="text"
                  autoComplete="name"
                  className={inputClass}
                />
              </Field>

              <Field label="Pharmacy name" error={errors.pharmacyName?.message}>
                <input
                  {...register('pharmacyName')}
                  type="text"
                  autoComplete="organization"
                  className={inputClass}
                />
              </Field>

              <Field label="Work email" error={errors.workEmail?.message}>
                <input
                  {...register('workEmail')}
                  type="email"
                  autoComplete="email"
                  className={inputClass}
                />
              </Field>

              <Field label="Phone (optional)" error={errors.phone?.message}>
                <input
                  {...register('phone')}
                  type="tel"
                  autoComplete="tel"
                  className={inputClass}
                />
              </Field>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Pharmacy management system" error={errors.pms?.message}>
                  <select {...register('pms')} className={inputClass} defaultValue="">
                    <option value="" disabled>
                      Select…
                    </option>
                    {PMS_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Deliveries / month" error={errors.volume?.message}>
                  <select {...register('volume')} className={inputClass} defaultValue="">
                    <option value="" disabled>
                      Select…
                    </option>
                    {VOLUME_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Anything specific you'd like to know? (optional)" error={errors.notes?.message}>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className={cn(inputClass, 'resize-none')}
                />
              </Field>

              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-2 inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-dark disabled:opacity-60"
              >
                {isSubmitting ? 'Sending…' : 'Request a demo'}
              </button>
              <p className="text-center text-xs text-ink-3">
                We'll never share your information. No marketing email — only a reply from a person.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

const inputClass =
  'block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-ink shadow-sm outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary';

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-ink">{label}</span>
      {children}
      {error ? <span className="mt-1 block text-xs text-red-600">{error}</span> : null}
    </label>
  );
}
